import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, HelpCircle, RefreshCw, X } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { formatDate, formatTime } from '@/shared/lib/format';
import { fetchClients } from '@/features/clients/api/use-clients';
import { fetchAllCampaigns } from '@/features/campaigns/api/use-campaigns';
import { CHANNEL_META, channelLabel } from '@/features/marketing-shared/model/channels';
import { fetchDispatchRuns, type DispatchRun } from '../api/use-dispatch-runs';

// Só os canais que já passam pelo worker (push, WhatsApp núcleo e e-mail legado).
const CHANNEL_FILTERS = [
  { value: 'all', label: 'Todos os canais' },
  { value: 'push', label: CHANNEL_META.push.label },
  { value: 'whatsapp', label: CHANNEL_META.whatsapp.label },
  { value: 'email', label: CHANNEL_META.email.label },
] as const;

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos os status' },
  { value: 'error', label: 'Erro' },
  { value: 'success', label: 'Sucesso' },
  { value: 'running', label: 'Travado / em andamento' },
] as const;

const WINDOW_FILTERS = [
  { value: '1', label: 'Hoje' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
] as const;

function statusMeta(status?: string): { variant: BadgeProps['variant']; label: string; icon: typeof AlertTriangle } {
  if (status === 'error') return { variant: 'destructive', label: 'Erro', icon: AlertTriangle };
  if (status === 'success') return { variant: 'success', label: 'Sucesso', icon: CheckCircle2 };
  if (status === 'running') return { variant: 'warning', label: 'Travado / rodando', icon: Clock };
  return { variant: 'secondary', label: 'Anterior ao fix (sem status)', icon: HelpCircle };
}

/**
 * Log dos disparos que já passam pelo worker (Push, WhatsApp núcleo e e-mail
 * legado) — GET /dispatch_runs. Aba de Observabilidade; `?campanha=` filtra
 * uma campanha vinda da agenda.
 */
export function DispatchRunsPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const campaignFilter = params.get('campanha');
  const clearCampaign = () => {
    const next = new URLSearchParams(params);
    next.delete('campanha');
    setParams(next, { replace: true });
  };
  const [channel, setChannel] = useState('all');
  const [status, setStatus] = useState('all');
  const [windowDays, setWindowDays] = useState('7');
  const [clientId, setClientId] = useState('all');

  const from = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - Number(windowDays));
    return d.toISOString();
  }, [windowDays]);

  const isAdmin = user?.role === 'Admin';

  const clientsQuery = useQuery({
    queryKey: ['clients', 'all-for-dispatch-runs'],
    queryFn: fetchClients,
    enabled: isAdmin,
    staleTime: 60 * 60 * 1000,
  });

  const clientOptions = useMemo(() => {
    return [...(clientsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [clientsQuery.data]);

  const selectedClientPassbookIds = useMemo(() => {
    if (clientId === 'all') return undefined;
    const client = (clientsQuery.data ?? []).find((c) => c._id === clientId);
    return (client?.passbooks ?? []).map((pb) => String(pb._id)).filter(Boolean);
  }, [clientId, clientsQuery.data]);

  const runsQuery = useQuery({
    queryKey: ['dispatch-runs', channel, status, windowDays, clientId, selectedClientPassbookIds],
    queryFn: () =>
      fetchDispatchRuns({
        tipo_disparo: channel === 'all' ? undefined : channel,
        status: status === 'all' ? undefined : status,
        from,
        limit: 300,
        idPassbooks: selectedClientPassbookIds,
      }),
    enabled: isAdmin && (clientId === 'all' || Boolean(clientsQuery.data)),
  });

  const campaignsQuery = useQuery({
    queryKey: ['campaigns', 'all-for-dispatch-runs'],
    queryFn: fetchAllCampaigns,
    enabled: isAdmin,
    staleTime: 60 * 60 * 1000,
  });

  const passbookName = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clientsQuery.data ?? []) {
      for (const pb of client.passbooks ?? []) {
        if (pb._id) map.set(String(pb._id), client.name || 'Cliente desconhecido');
      }
    }
    return map;
  }, [clientsQuery.data]);

  const campaignName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of campaignsQuery.data ?? []) {
      map.set(String(c._id), c.nome || 'Campanha sem nome');
    }
    return map;
  }, [campaignsQuery.data]);

  const allRows: DispatchRun[] = runsQuery.data ?? [];
  const rows = campaignFilter ? allRows.filter((run) => run.id_campanha === campaignFilter) : allRows;
  const isLoading = runsQuery.isLoading || clientsQuery.isLoading || campaignsQuery.isLoading;
  const isRefreshing =
    runsQuery.isFetching || clientsQuery.isFetching || campaignsQuery.isFetching;

  const handleForceRefresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['dispatch-runs'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'all-for-dispatch-runs'] }),
      queryClient.invalidateQueries({ queryKey: ['clients', 'all-for-dispatch-runs'] }),
    ]);
  };

  return (
    <>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-auto min-w-[9.5rem] gap-1.5 pr-3">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            {CHANNEL_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-auto min-w-[11rem] gap-1.5 pr-3">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="w-auto min-w-[9rem] gap-1.5 pr-3">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-auto min-w-[11rem] gap-1.5 pr-3">
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os clientes</SelectItem>
            {clientOptions.map((c) => (
              <SelectItem key={c._id} value={c._id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {campaignFilter ? (
          <Badge variant="accent" className="gap-1 font-normal">
            {campaignName.get(campaignFilter) ?? 'Campanha'}
            <button type="button" aria-label="Tirar filtro de campanha" onClick={clearCampaign} className="rounded-sm hover:text-foreground">
              <X className="size-3" />
            </button>
          </Badge>
        ) : null}

        <Button variant="outline" size="sm" className="ml-auto" disabled={isRefreshing} onClick={() => void handleForceRefresh()}>
          <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      <div className="glass overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="whitespace-nowrap px-4 py-3 font-medium">Quando</th>
                <th className="px-4 py-3 font-medium">Loja</th>
                <th className="px-4 py-3 font-medium">Campanha</th>
                <th className="px-4 py-3 font-medium">Canal</th>
                <th className="px-4 py-3 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    Público <Tooltip content="Pessoas selecionadas pela campanha." />
                  </span>
                </th>
                <th className="px-4 py-3 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    Tentativas{' '}
                    <Tooltip content="Um envio por dispositivo: quem tem app e cartão na carteira recebe 2. Por isso pode passar do Público. Tentativas = Entregues + Falhas." />
                  </span>
                </th>
                <th className="px-4 py-3 text-right font-medium">Entregues</th>
                <th className="px-4 py-3 text-right font-medium">Falhas</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Carregando…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Nenhum disparo no período/filtro selecionado.
                  </td>
                </tr>
              ) : (
                rows.map((run) => {
                  const meta = statusMeta(run.status);
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={run._id}
                      onClick={() => navigate(`/dispatch-runs/${run._id}`)}
                      className="cursor-pointer border-b border-border/40 align-top transition-colors last:border-0 hover:bg-muted/40"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                        {run.created_at ? `${formatDate(run.created_at)} ${formatTime(run.created_at)}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {run.id_passbook
                          ? (passbookName.get(run.id_passbook) ?? `Loja ${run.id_passbook.slice(-6)}`)
                          : '—'}
                      </td>
                      <td className="max-w-[16rem] truncate px-4 py-3">
                        {run.id_campanha ? (campaignName.get(run.id_campanha) ?? '—') : '—'}
                      </td>
                      <td className="px-4 py-3">{channelLabel(run.tipo_disparo) || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{run.total ?? 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{run.sent}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{Math.max(run.sent - run.failed, 0)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {run.failed > 0 ? <span className="text-destructive">{run.failed}</span> : run.failed}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant={meta.variant} className="w-fit">
                            <Icon className="size-3" /> {meta.label}
                          </Badge>
                          {run.error ? (
                            <span
                              className="max-w-[14rem] truncate text-xs text-muted-foreground"
                              title={run.error}
                            >
                              {run.error}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
